/* tslint:disable:a11y-role-has-required-aria-props */
import * as React from 'react';
import cx from 'classnames';
import { Input, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { CompetitionPledge, CompetitionPledgeCategories, PledgeCompletions, CompetitionScoreLevel } from '../userApi';
import { LProps, withLocalization } from '../utils/wrappers';
import { Link } from 'react-router-dom';
import { makePledgePath, makePledgeCategoryPath } from '../utils/routerHelper';
import { State } from '../redux/state';
import * as selectors from '../redux/selectors';
import { PledgePopularityBadges } from '../redux/selectors/pledges';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  getPledgePhoto, getScoreLevelPhoto, getCompetitionUserScore, calculatePointsForNextStory,
  fallbackPledgePhotoOnError
} from '../utils/userApiHelper';

interface ManualProps {
  competitionPledgeCategoryID: number;
  changeComponent: React.ReactNode;

}

interface PledgesByScoreLevel {
  scoreLevel?: CompetitionScoreLevel;
  pledges: CompetitionPledge[];
  active: boolean;
}

interface Props extends ManualProps {
  pledgesByLevel: PledgesByScoreLevel[];
  categories: CompetitionPledgeCategories;
  completions: PledgeCompletions;
  userIsSomeUniversityAdmin: boolean;
  canBypassPledgeLocks: boolean;
  userPoints: number;
  /** Pledge IDs the current user can take (score + calculator onboarding). */
  accessiblePledgeIds: { [pledgeId: number]: boolean };
  featuredPledgeUUIDs: string[];
  featuredPledgeLabel?: string;
  completionTotals: { [pledgeId: number]: number };
}

interface PledgesCardsState {
  nameFilter: string;
  categoryFilter: string; // 'All' or category ID
  completedFilter: 'All' | 'Completed' | 'Not Completed';
  pointsMinFilter: string;
  pointsMaxFilter: string;
  lockedLevelModal: CompetitionScoreLevel | null;
  calculatorGateModalOpen: boolean;
}

const initialState: PledgesCardsState = {
  nameFilter: '',
  categoryFilter: 'All',
  completedFilter: 'All',
  pointsMinFilter: '',
  pointsMaxFilter: '',
  lockedLevelModal: null,
  calculatorGateModalOpen: false
};

class PledgesCards extends React.Component<LProps<Props>, PledgesCardsState> {
  public state = initialState;

  private filterPledges(pledges: CompetitionPledge[]): CompetitionPledge[] {
    const { categories, completions } = this.props;
    const { nameFilter, categoryFilter, completedFilter, pointsMinFilter, pointsMaxFilter } = this.state;
    const minPoints = parseInt(pointsMinFilter, 10);
    const maxPoints = parseInt(pointsMaxFilter, 10);

    return pledges.filter((pledge) => {
      if (nameFilter && !pledge.shortTitle.toLowerCase().includes(nameFilter.toLowerCase())) return false;
      if (categoryFilter !== 'All' && pledge.competitionPledgeCategoryID && pledge.competitionPledgeCategoryID.toString() !== categoryFilter) return false;
      const isCompleted = (completions[pledge.id] || 0) >= pledge.repeats;
      if (completedFilter === 'Completed' && !isCompleted) return false;
      if (completedFilter === 'Not Completed' && isCompleted) return false;
      if (!isNaN(minPoints) && pledge.points < minPoints) return false;
      if (!isNaN(maxPoints) && pledge.points > maxPoints) return false;
      return true;
    });
  }

  private pointsNeededForLevel(scoreLevel: CompetitionScoreLevel): number {
    return Math.max(0, (scoreLevel.threshold || 0) - this.props.userPoints);
  }

  private renderScoreLevelHeader(scoreLevel: CompetitionScoreLevel, active: boolean) {
    const { t, formatNumber, userIsSomeUniversityAdmin } = this.props;
    const pointsNeeded = this.pointsNeededForLevel(scoreLevel);
    const showPointsHint = !active && !this.props.canBypassPledgeLocks && pointsNeeded > 0;
    return (
      <div className={cx('cc__pledges_cards_level_header', {
        'cc__pledges_cards_level_header--active': active,
        'cc__pledges_cards_level_header--inactive': !active
      })}>
        <div className="cc__pledges_cards_level_header__title">
          {getScoreLevelPhoto(scoreLevel) ? (
            <img src={getScoreLevelPhoto(scoreLevel)} alt={scoreLevel.name} title={scoreLevel.name} />
          ) : (
            <span className="cc__pledges_cards_level_header__placeholder" />
          )}
          <span>{scoreLevel.name}</span>
          {showPointsHint &&
            <span className="cc__pledges_level_points_hint">
              {t('({{more}} more points to unlock, {{total}} total)', {
                more: formatNumber(pointsNeeded),
                total: formatNumber(scoreLevel.threshold || 0)
              })}
            </span>
          }
        </div>
      </div>
    );
  }

  private openLockedLevelModal = (scoreLevel: CompetitionScoreLevel) => {
    this.setState({ lockedLevelModal: scoreLevel, calculatorGateModalOpen: false });
  }

  private openCalculatorGateModal = () => {
    this.setState({ calculatorGateModalOpen: true, lockedLevelModal: null });
  }

  private closeLockedLevelModal = () => {
    this.setState({ lockedLevelModal: null });
  }

  private closeCalculatorGateModal = () => {
    this.setState({ calculatorGateModalOpen: false });
  }

  private renderLockedLevelModal() {
    const { lockedLevelModal } = this.state;
    const { t, formatNumber } = this.props;
    if (!lockedLevelModal) return null;

    const pointsNeeded = this.pointsNeededForLevel(lockedLevelModal);

    return (
      <Modal isOpen={true} toggle={this.closeLockedLevelModal}>
        <ModalHeader toggle={this.closeLockedLevelModal}>
          {lockedLevelModal.name}
        </ModalHeader>
        <ModalBody>
          <div className="cc__pledge_locked_modal__level">
            {getScoreLevelPhoto(lockedLevelModal) ? (
              <img src={getScoreLevelPhoto(lockedLevelModal)} alt={lockedLevelModal.name} title={lockedLevelModal.name} />
            ) : (
              <span className="cc__pledges_cards_level_header__placeholder" />
            )}
            <span>{lockedLevelModal.name}</span>
          </div>
          <p>
            {t('{{more}} more points to unlock, {{total}} total', {
              more: formatNumber(pointsNeeded),
              total: formatNumber(lockedLevelModal.threshold || 0)
            })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.closeLockedLevelModal}>{t('Got it!')}</Button>
        </ModalFooter>
      </Modal>
    );
  }

  private renderCalculatorGateModal() {
    const { calculatorGateModalOpen } = this.state;
    const { t } = this.props;
    if (!calculatorGateModalOpen) return null;

    return (
      <Modal isOpen={true} toggle={this.closeCalculatorGateModal}>
        <ModalHeader toggle={this.closeCalculatorGateModal}>
          {t('Action locked')}
        </ModalHeader>
        <ModalBody>
          <p>{t('Introduce yourself to unlock actions and keep going.')}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.closeCalculatorGateModal}>{t('Got it!')}</Button>
        </ModalFooter>
      </Modal>
    );
  }

  private renderPledgeCard(
    pledge: CompetitionPledge,
    scoreLevel: CompetitionScoreLevel,
    active: boolean,
    popularityBadges: PledgePopularityBadges
  ) {
    const {
      t, formatNumber, categories, completions, userIsSomeUniversityAdmin, accessiblePledgeIds,
      featuredPledgeUUIDs, featuredPledgeLabel
    } = this.props;
    const isFeatured = featuredPledgeUUIDs.indexOf(pledge.uuid) >= 0;
    const isMostPopular = Boolean(popularityBadges.mostPopular[pledge.id]);
    const isPopular = Boolean(popularityBadges.popular[pledge.id]);
    const scoreLocked = !active && !this.props.canBypassPledgeLocks;
    const gateLocked = !accessiblePledgeIds[pledge.id] && !scoreLocked && !this.props.canBypassPledgeLocks;
    const isLocked = scoreLocked || gateLocked;
    const pointsNeeded = this.pointsNeededForLevel(scoreLevel);
    const isCompleted = (completions[pledge.id] || 0) >= pledge.repeats;
    const completedCount = completions[pledge.id] || 0;
    const nextPoints = isCompleted ? 0 : calculatePointsForNextStory(completions, pledge);
    const photo = getPledgePhoto(pledge, categories[pledge.competitionPledgeCategoryID] && categories[pledge.competitionPledgeCategoryID].name);

    const card = (
      <div className={cx('cc__white_box cc__pledge_card', {
        'cc__pledge_card--completed': isCompleted,
        'cc__pledge_card--inactive': isLocked,
        'cc__pledge_card--locked': isLocked,
        'cc__pledge_card--featured': isFeatured,
        'cc__pledge_card--most_popular': isMostPopular && !isFeatured,
        'cc__pledge_card--popular': isPopular && !isFeatured && !isMostPopular
      })}>
        {(isFeatured || isMostPopular || isPopular) && (
          <div className="cc__pledge_card__designation_banners">
            {isFeatured &&
              <span className="cc__pledge_designation cc__pledge_designation--featured">
                {featuredPledgeLabel || t('Featured action')}
              </span>
            }
            {isMostPopular &&
              <span className="cc__pledge_designation cc__pledge_designation--most_popular">
                {t('Most Popular')}
              </span>
            }
            {isPopular &&
              <span className="cc__pledge_designation cc__pledge_designation--popular">
                {t('Popular')}
              </span>
            }
          </div>
        )}
        {photo && (
          <div className="cc__pledge_card__image">
            <img
              src={photo}
              alt={pledge.shortTitle}
              title={pledge.shortTitle}
              onError={(e) => fallbackPledgePhotoOnError(
                e,
                categories[pledge.competitionPledgeCategoryID] && categories[pledge.competitionPledgeCategoryID].name
              )}
            />
          </div>
        )}
        <div className="cc__pledge_card__header">
          {isLocked ? (
            <span className="cc__pledge_card__title">{pledge.shortTitle}</span>
          ) : (
            <Link to={makePledgePath(pledge, t)} className="cc__pledge_card__title">
              {pledge.shortTitle}
            </Link>
          )}
        </div>
        {pledge.competitionPledgeCategoryID && categories[pledge.competitionPledgeCategoryID] && (
          <div className="cc__pledge_card__category">
            {isLocked ? (
              <span>{categories[pledge.competitionPledgeCategoryID].name}</span>
            ) : (
              <Link to={makePledgeCategoryPath(pledge.competitionPledgeCategoryID, t)}>
                {categories[pledge.competitionPledgeCategoryID].name}
              </Link>
            )}
          </div>
        )}
        <div className="cc__pledge_card__stats">
          <div className="cc__pledge_card__stat">
            <strong>{isCompleted ? '—' : nextPoints}</strong> {t('Points')}
          </div>
          <div className="cc__pledge_card__stat">
            <strong>{formatNumber(pledge.savingsCO2Lbs, 0)}</strong> {t('Lbs CO')}<sub>2</sub>
          </div>
        </div>
        <p className="cc__pledge_card__photo_bonus">
          <strong>{t('Photo or Video earns 50 Bonus points')}</strong>
        </p>
        {scoreLocked && pointsNeeded > 0 && (
          <p className="cc__pledge_card__lock_hint">
            {t('({{more}} more points to unlock, {{total}} total)', {
              more: formatNumber(pointsNeeded),
              total: formatNumber(scoreLevel.threshold || 0)
            })}
          </p>
        )}
        {gateLocked && (
          <p className="cc__pledge_card__lock_hint">
            {t('(Introduce yourself to unlock)')}
          </p>
        )}
        <div className="cc__pledge_card__footer">
          <div className="cc__pledge_card__completions">
            {isCompleted ? (
              <span className="text-success"><i className="fa fa-check" /> {t('Completed')}</span>
            ) : (
              <span>{t('Completions:')} {completedCount} / {pledge.repeats}</span>
            )}
          </div>
          {!isLocked && (
            <Link className="btn btn-sm btn-outline-primary" to={makePledgePath(pledge, t)}>
              {t('View Details')}
            </Link>
          )}
        </div>
      </div>
    );

    if (!isLocked) {
      return <div key={pledge.id} className="cc__pledge_card_wrapper">{card}</div>;
    }

    const onLockedActivate = () => {
      if (scoreLocked) {
        this.openLockedLevelModal(scoreLevel);
      } else {
        this.openCalculatorGateModal();
      }
    };

    return (
      <div
        key={pledge.id}
        role="button"
        tabIndex={0}
        className={cx('cc__pledge_card_wrapper', 'cc__pledge_card_wrapper--locked-clickable', 'cc__pledge_card_wrapper--locked')}
        onClick={onLockedActivate}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onLockedActivate();
          }
        }}
      >
        {card}
      </div>
    );
  }

  public render() {
    const {
      pledgesByLevel, t, categories, changeComponent, completionTotals
    } = this.props;
    const { categoryFilter } = this.state;

    const pledgesByFilteredLevel = pledgesByLevel
      .map((level) => ({
        ...level,
        pledges: this.filterPledges(level.pledges)
      }))
      .filter((level) => level.pledges.length > 0);

    // Hide empty-name categories (system/onboarding bucket) from the filter.
    const categoryOptions = Object.keys(categories)
      .map((id) => categories[id])
      .filter((cat) => cat && String(cat.name || '').trim().length > 0);

    return (
      <div className="cc__pledges_cards_container">
        <div className="cc__pledges_filters cc__white_box">
          <div className="cc__pledges_filters__group">
            <label>{t('Search by Name')}</label>
            <Input
              type="text"
              placeholder={t('Pledge Name')}
              value={this.state.nameFilter}
              onChange={(e) => this.setState({ nameFilter: e.target.value })}
            />
          </div>
          <div className="cc__pledges_filters__group">
            <label>{t('Category')}</label>
            <Input
              type="select"
              value={categoryFilter}
              onChange={(e) => this.setState({ categoryFilter: e.target.value })}
            >
              <option value="All">{t('All Categories')}</option>
              {categoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </Input>
          </div>
          <div className="cc__pledges_filters__group">
            <label>{t('Status')}</label>
            <Input
              type="select"
              value={this.state.completedFilter}
              onChange={(e) => this.setState({ completedFilter: e.target.value as any })}
            >
              <option value="All">{t('All')}</option>
              <option value="Completed">{t('Completed')}</option>
              <option value="Not Completed">{t('Not Completed')}</option>
            </Input>
          </div>
          <div className="cc__pledges_filters__group cc__pledges_filters__points">
            <label>{t('Points Range')}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Input
                type="number"
                placeholder={t('Min')}
                value={this.state.pointsMinFilter}
                onChange={(e) => this.setState({ pointsMinFilter: e.target.value })}
                min={0}
              />
              <Input
                type="number"
                placeholder={t('Max')}
                value={this.state.pointsMaxFilter}
                onChange={(e) => this.setState({ pointsMaxFilter: e.target.value })}
                min={0}
              />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          {changeComponent}
        </div>
        {pledgesByFilteredLevel.length === 0 ? (
          <div className="cc__pledges_empty">{t('No pledges found matching your filters.')}</div>
        ) : (
          pledgesByFilteredLevel.map(({ scoreLevel, pledges, active }) => {
            const popularityBadges = selectors.pledgePopularityBadgesInSection(pledges, completionTotals);
            return (
            <div
              key={scoreLevel.id}
              className={cx('cc__pledges_cards_level_section', {
                'cc__pledges_cards_level_section--active': active,
                'cc__pledges_cards_level_section--inactive': !active
              })}
            >
              {this.renderScoreLevelHeader(scoreLevel, active)}
              <div className="cc__pledges_cards_grid">
                {pledges.map((pledge) => this.renderPledgeCard(pledge, scoreLevel, active, popularityBadges))}
              </div>
            </div>
            );
          })
        )}
        {this.renderLockedLevelModal()}
        {this.renderCalculatorGateModal()}
      </div>
    );
  }
}

const mapStateToProps = (state: State, ownProps: ManualProps) => {
  const user = state.user;
  const competitionUserScore = user
    ? getCompetitionUserScore(state.competitionUserScores, user.id)
    : undefined;
  const accessiblePledgeIds: { [pledgeId: number]: boolean } = {};
  Object.keys(state.competitionPledges || {}).forEach((id) => {
    const pledge = state.competitionPledges[Number(id)];
    if (pledge) {
      accessiblePledgeIds[pledge.id] = selectors.isPledgeAccessible(state, pledge);
    }
  });
  return {
    pledgesByLevel: selectors.pledgesByCategoryAndLevel(state, ownProps),
    categories: state.competitionPledgeCategories,
    completions: state.userPledgeCompletions,
    userIsSomeUniversityAdmin: selectors.userIsSomeUniversityAdmin(state),
    canBypassPledgeLocks: selectors.canBypassPledgeParticipantRestrictions(state),
    userPoints: competitionUserScore ? competitionUserScore.points : 0,
    accessiblePledgeIds,
    featuredPledgeUUIDs: selectors.getFeaturedPledgeUUIDs(state),
    featuredPledgeLabel: state.competition && state.competition.config
      ? state.competition.config.featuredPledgeLabel
      : undefined,
    completionTotals: selectors.getCompetitionPledgeCompletionTotals(state)
  };
};

export default compose(
  withLocalization('pledges'),
  connect(mapStateToProps)
)(PledgesCards) as React.SFC<ManualProps>;
